import { Injectable, inject } from '@angular/core';
import { LoginResponse, OidcSecurityService } from 'angular-auth-oidc-client';
import { Observable, ReplaySubject, throwError } from 'rxjs';
import { LoggerService } from '../../../shared/services/logger.service';
import { AuthOfflineService } from './auth-offline.service';
import { AuthService } from './auth.service';

/**
 * Coordena renovação de sessão OIDC com mutex e falha limpa.
 *
 * Evita o loop de POST /token 400 quando o refresh token está inválido:
 * uma única tentativa compartilhada; em falha, limpa storage local e
 * redireciona ao login uma única vez.
 */
@Injectable({
  providedIn: 'root',
})
export class TokenRefreshCoordinator {
  private readonly oidcSecurityService = inject(OidcSecurityService);
  private readonly authService = inject(AuthService);
  private readonly offlineService = inject(AuthOfflineService);
  private readonly logger = inject(LoggerService);

  private inFlight$: Observable<LoginResponse> | null = null;
  private reauthInProgress = false;

  /**
   * Renova a sessão. Chamadas concorrentes compartilham a mesma Observable.
   * Em falha definitiva: limpa estado local e inicia reauth uma vez.
   */
  refreshSession(): Observable<LoginResponse> {
    if (this.reauthInProgress) {
      return throwError(() => new Error('SESSION_REAUTH_IN_PROGRESS'));
    }

    if (this.inFlight$) {
      return this.inFlight$;
    }

    const subject = new ReplaySubject<LoginResponse>(1);
    this.inFlight$ = subject.asObservable();

    this.oidcSecurityService.forceRefreshSession().subscribe({
      next: result => {
        if (!result.isAuthenticated) {
          this.beginReauth('Sessão inválida após tentativa de refresh');
        }
        subject.next(result);
        subject.complete();
      },
      error: err => {
        this.beginReauth('Falha ao renovar token (refresh inválido ou expirado)', err);
        subject.error(err);
      },
      complete: () => {
        // Caso a lib complete sem next (defensivo)
        if (!subject.closed) {
          subject.complete();
        }
      },
    });

    // Libera o mutex após terminar (microtask mantém compartilhamento no mesmo tick)
    const release = () => {
      queueMicrotask(() => {
        this.inFlight$ = null;
      });
    };
    subject.subscribe({ error: release, complete: release });

    return this.inFlight$;
  }

  /**
   * Limpa tokens/perfil locais sem redirecionar (bootstrap / checkAuth).
   */
  clearLocalSession(reason: string): void {
    this.logger.authEvent(`Limpando sessão local: ${reason}`);
    this.authService.clearLocalUserState();
    this.offlineService.clearUserProfile().catch(err => {
      this.logger.error('Erro ao limpar cache offline após sessão inválida', err);
    });
    this.oidcSecurityService.logoffLocal();
  }

  /**
   * Indica se um redirect de reauth já foi disparado nesta instância da app.
   */
  isReauthInProgress(): boolean {
    return this.reauthInProgress;
  }

  private beginReauth(reason: string, error?: unknown): void {
    if (this.reauthInProgress) {
      return;
    }

    this.reauthInProgress = true;
    this.logger.warn(`[AUTH] ${reason}`, error);

    sessionStorage.setItem(
      'session_expired_message',
      'Sua sessão expirou. Por favor, faça login novamente.',
    );

    this.clearLocalSession(reason);
    this.oidcSecurityService.authorize();
  }
}
